import React, { useState } from 'react';
import { ConfigProvider, theme, Table, Avatar, Button } from 'antd';
import {
    Plus,
    Menu,
    Tag,
    ArrowRightToLine,
    ArrowLeftToLine,
    Settings,
    ArrowUpDown,
    LayoutList,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    Check
} from 'lucide-react';
import Layout from '../../Layout'
import { useNavigate, Link } from 'react-router-dom';

export default function PurchaseOrderList() {
    const [isDarkMode, setIsDarkMode] = useState(false);
    const [activeTab, setActiveTab] = useState('all');
    const navigate = useNavigate();

    // Cấu hình Theme Ant Design
    const antdThemeConfig = {
        token: {
            colorPrimary: '#3e91ab',
            colorBgContainer: isDarkMode ? '#1f2937' : '#ffffff',
            colorText: isDarkMode ? '#f3f4f6' : '#374151',
            borderRadius: 4,
        },
        components: {
            Table: {
                headerBg: isDarkMode ? '#374151' : '#f9fafb',
                headerColor: isDarkMode ? '#d1d5db' : '#6b7280',
                headerBorderRadius: 0,
                rowHoverBg: isDarkMode ? '#4b5563' : '#f9fafb',
                paddingContentVertical: 8,
            }
        },
        algorithm: isDarkMode ? theme.darkAlgorithm : theme.defaultAlgorithm,
    };

    // ================= MOCK DATA (Khớp với hình ảnh) =================
    const dataSource = [
        { key: '1', creator: { text: '1', color: '#ff6b6b' }, code: 'ĐHM_2026_0298', supplier: 'CÔNG TY TNHH VIỆT HÀ', value: '5.000.000 ₫', paid: null, status: 'pending', step: { text: 'Admin hệ thống kiểm tra', status: 'pending' }, date: '17/08/2026' },
        { key: '2', creator: { img: 'https://i.pravatar.cc/150?u=1' }, code: 'ĐHM_2026_0297', supplier: 'Công Ty TNHH Tmdv Dầu Khí Hoàng Thủy', value: '1.800.000 ₫', paid: null, status: 'approved', step: { text: 'Trưởng phòng duyệt', status: 'approved' }, date: '06/08/2026' },
        { key: '3', creator: { text: 'T', color: '#0ea5e9' }, code: 'ĐHM_2026_0296', supplier: null, value: '1.000.000 ₫', paid: null, status: 'approved', step: { text: 'Trưởng phòng duyệt', status: 'approved' }, date: '28/07/2026' },
        { key: '4', creator: { text: 'D', color: '#eab308' }, code: 'ĐHM_2026_0295', supplier: 'CÔNG TY CỔ PHẦN ĐẦU TƯ THƯƠNG MẠI SPC VIỆT NAM', value: '30.013.200 ₫', paid: null, status: 'approved', step: { text: 'Admin hệ thống kiểm tra', status: 'approved' }, date: '23/07/2026' },
        { key: '5', creator: { text: 'S', color: '#f97316' }, code: 'ĐHM_2026_0294', supplier: null, value: '3.000 ₫', paid: null, status: 'pending', step: { text: 'Trưởng phòng duyệt', status: 'pending' }, date: '12/07/2026' },
        { key: '6', creator: { text: 'S', color: '#f97316' }, code: 'ĐHM_2026_0293', supplier: null, value: '50.400.000 ₫', paid: null, status: 'pending', step: { text: 'Trưởng phòng duyệt', status: 'pending' }, date: '12/07/2026' },
        { key: '7', creator: { text: 'S', color: '#f97316' }, code: 'ĐHM_2026_0292', supplier: 'Đào Công Danh', value: '38.115 ₫', paid: null, status: 'pending', step: { text: 'Trưởng phòng duyệt', status: 'pending' }, date: '24/06/2026' },
        { key: '8', creator: { img: 'https://i.pravatar.cc/150?u=2' }, code: 'ĐHM_2025_0250', supplier: null, value: '33.400.000 ₫', paid: '33.400.000 ₫', status: 'approved', step: { text: 'Admin hệ thống kiểm tra', status: 'approved' }, date: '05/06/2026' },
        { key: '9', creator: { text: 'T', color: '#0ea5e9' }, code: 'ĐHM_2026_0291', supplier: 'Trần Thị Huệ', value: '389.500 ₫', paid: null, status: 'approved', step: { text: 'Admin hệ thống kiểm tra', status: 'approved' }, date: '03/06/2026' },
        { key: '10', creator: { text: 'T', color: '#0ea5e9' }, code: 'ĐHM_2026_0290', supplier: null, value: '2.000.000 ₫', paid: null, status: 'approved', step: { text: 'Admin hệ thống kiểm tra', status: 'approved' }, date: '03/06/2026' },
    ];

    // ================= ĐỊNH NGHĨA CỘT =================
    const columns = [
        {
            title: 'Người tạo',
            dataIndex: 'creator',
            key: 'creator',
            width: 80,
            align: 'center',
            render: (creator) => (
                <Link to="/purchase-order/detail" className="inline-block transition-opacity hover:opacity-80">
                    {creator.img ? (
                        <Avatar src={creator.img} size={28} className="cursor-pointer" />
                    ) : (
                        <Avatar style={{ backgroundColor: creator.color }} size={28} className="cursor-pointer text-xs font-medium">
                            {creator.text}
                        </Avatar>
                    )}
                </Link>
            ),
        },
        { title: 'Mã đơn hàng mua', dataIndex: 'code', key: 'code', className: 'font-medium whitespace-nowrap' },
        { title: 'Nhà cung cấp', dataIndex: 'supplier', key: 'supplier', render: (text) => text || <span className="text-gray-400">--</span>, className: 'whitespace-nowrap' },
        { title: 'Giá trị', dataIndex: 'value', key: 'value', align: 'right', className: 'whitespace-nowrap' },
        { title: 'Đã chi', dataIndex: 'paid', key: 'paid', align: 'right', render: (text) => text || <span className="text-gray-400">--</span>, className: 'whitespace-nowrap' },
        {
            title: 'Trạng thái',
            dataIndex: 'status',
            key: 'status',
            align: 'center',
            width: 120,
            render: (status) => {
                const isApproved = status === 'approved';
                return (
                    <span className={`px-2 py-1 rounded text-[11px] font-medium inline-block w-20 text-center whitespace-nowrap ${isApproved
                        ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-orange-100 text-orange-500 dark:bg-orange-900/30 dark:text-orange-400'
                        }`}>
                        {isApproved ? 'Đã duyệt' : 'Chờ duyệt'}
                    </span>
                )
            },
        },
        {
            title: 'Bước duyệt',
            dataIndex: 'step',
            key: 'step',
            width: 200,
            render: (step) => {
                const isApproved = step.status === 'approved';
                return (
                    <div className={`px-2 py-1 border rounded text-[11px] font-medium inline-flex items-center gap-1 whitespace-nowrap ${isApproved
                        ? 'border-green-300 text-green-500 bg-green-50 dark:bg-green-900/10'
                        : 'border-orange-300 text-orange-500 bg-orange-50 dark:bg-orange-900/10'
                        }`}>
                        {step.text} {isApproved ? <Check size={12} /> : <ChevronRight size={12} />}
                    </div>
                )
            },
        },
        { title: 'Ngày mua', dataIndex: 'date', key: 'date', align: 'right', className: 'whitespace-nowrap' },
    ];

    return (
        <Layout>
            <div className="flex gap-2 mb-4 flex justify-between">
                <h2 className="text-lg text-gray-900 font-semibold dark:text-white">
                    Danh sách đơn hàng mua
                </h2>
                <div className="flex items-center gap-4 shrink-0 flex sm:hidden">
                    <Button
                        type="primary"
                        icon={<Plus />}
                        className="bg-[#3e91ab] hover:!bg-[#357f96] border-[#3e91ab]"
                        onClick={() => navigate(`/purchase-order/add`)}
                    >
                        Thêm mới
                    </Button>
                </div>
            </div>

            <ConfigProvider theme={antdThemeConfig}>
                <div className={`purchase-order-list-page min-h-screen transition-colors duration-300`}>

                    {/* Box chứa bảng */}
                    <div className={`w-full overflow-hidden flex flex-col rounded-md`}>

                        {/* HEADER TABS & NÚT THÊM MỚI */}
                        <div className={`flex justify-between items-end pt-3 border-b dark:border-[#374151]`}>
                            <div className="flex gap-2 items-center w-full overflow-x-auto no-scrollbar">
                                <div className="flex gap-6 text-sm font-medium">
                                    <div onClick={() => setActiveTab('all')} className={`cursor-pointer pb-3 border-b-2 transition-colors whitespace-nowrap ${activeTab === 'all' ? 'border-[#3e91ab] text-[#3e91ab]' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'}`}>Tất cả (160)</div>
                                    <div onClick={() => setActiveTab('pending')} className={`cursor-pointer pb-3 border-b-2 transition-colors whitespace-nowrap ${activeTab === 'pending' ? 'border-[#3e91ab] text-[#3e91ab]' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'}`}>Chờ duyệt (32)</div>
                                    <div onClick={() => setActiveTab('approved')} className={`cursor-pointer pb-3 border-b-2 transition-colors whitespace-nowrap ${activeTab === 'approved' ? 'border-[#3e91ab] text-[#3e91ab]' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'}`}>Đã duyệt (128)</div>
                                    <div onClick={() => setActiveTab('department')} className={`cursor-pointer pb-3 border-b-2 transition-colors whitespace-nowrap ${activeTab === 'department' ? 'border-[#3e91ab] text-[#3e91ab]' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'}`}>Phòng ban bạn (81)</div>
                                    <div onClick={() => setActiveTab('mine')} className={`cursor-pointer pb-3 border-b-2 transition-colors whitespace-nowrap ${activeTab === 'mine' ? 'border-[#3e91ab] text-[#3e91ab]' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'}`}>Đơn hàng bạn tạo (0)</div>
                                </div>
                            </div>

                            <div className="flex items-center gap-4 pb-2 shrink-0 hidden sm:flex">
                                <Button
                                    type="primary"
                                    icon={<Plus />}
                                    className="bg-[#3e91ab] hover:!bg-[#357f96] border-[#3e91ab]"
                                    onClick={() => navigate(`/purchase-order/add`)}
                                >
                                    Thêm mới
                                </Button>
                            </div>
                        </div>

                        {/* THANH TOOLBAR & PHÂN TRANG */}
                        <div className={`flex flex-col xl:flex-row items-center py-2 text-xs md:text-sm text-black/80 border-b dark:border-[#374151] overflow-x-auto`}>

                            {/* Thay justify-end bằng ml-auto (đẩy sang phải), và dùng min-w-max để nội dung không bị rớt dòng */}
                            <div className="flex items-center gap-4 xl:gap-5 w-full xl:w-auto ml-auto min-w-max text-000000e0">
                                <span className="flex items-center gap-1.5 cursor-pointer hover:text-[#3e91ab] transition-colors"><Tag size={14} /> Nhãn</span>
                                <span className="flex items-center gap-1.5 cursor-pointer hover:text-[#3e91ab] transition-colors"><ArrowRightToLine size={14} /> Export</span>
                                <span className="flex items-center gap-1.5 cursor-pointer hover:text-[#3e91ab] transition-colors"><ArrowLeftToLine size={14} /> Import</span>
                                <span className="flex items-center gap-1.5 cursor-pointer hover:text-[#3e91ab] transition-colors"><Settings size={14} /> Cài đặt</span>
                            </div>
                        </div>

                        {/* BẢNG DỮ LIỆU */}
                        <div className="overflow-x-auto">
                            <Table
                                columns={columns}
                                dataSource={dataSource}
                                rowSelection={{ type: 'checkbox' }}
                                pagination={false}
                                size="small"
                                className="custom-ant-table min-w-[1100px]"
                                scroll={{ x: "max-content" }}
                            />
                        </div>

                    </div>
                </div>
            </ConfigProvider>
        </Layout>
    );
}